import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { Company, CompanyGateway, InvoiceItem } from '../types';
import { MainContainer } from '../cardStyles';
import RemoveInvoiceItemModal from '../../../../../components/Modal/RemoveInvoiceItemModal';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import AddInvoiceItemsModal from '../AddInvoiceItemsModal';
import TotaisSection from '../Sections/TotaisSection';
import EmissaoSection from '../Sections/EmissaoSection';
import FreteSection from '../Sections/FreteSection';
import InfoAdicionaisSection from '../Sections/InfoAdicionaisSection';
import PagamentoSection from '../Sections/PagamentoSection';
import DestinatarioSection from '../Sections/DestinatarioSection';
import IdentificacaoSection, { IdentificacaoValues } from '../Sections/IdentificacaoSection';
import ItensSection from '../Sections/ItensSection';
import { useItemTaxResolution } from '../useItemTaxResolution';
import { useEmissionPolling } from '../useEmissionPolling';

const InvoiceForm = () => {
  const { id: invoiceId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [emissoras, setEmissoras] = useState<CompanyGateway[]>([]);
  const [selectedDestinatario, setSelectedDestinatario] = useState<{ company: Company } | null>(null);
  const [cfops, setCfops] = useState<{ id: number; code: string; description: string }[]>([]);
  const [cfopsPerItem, setCfopsPerItem] = useState<Map<number, { id: number; code: string; description: string }[]>>(new Map());
  const [ncmOptions, setNcmOptions] = useState<{ id: number; ncm: string }[]>([]);

  const [invoice, setInvoice] = useState<any>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const isEmitida = invoice?.status && invoice.status !== 'RASCUNHO';
  const podeEditarItens = invoice?.status === 'RASCUNHO' || invoice?.status === 'NEGADA';
  const [removeItemTarget, setRemoveItemTarget] = useState<{ id: number; descricao: string; quantidade: number } | null>(null);
  const [showAddItems, setShowAddItems] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [identValues, setIdentValues] = useState<IdentificacaoValues>({
    companyEmissorId: '',
    naturezaOperacao: '',
    tipoOperacao: '',
    finalidade: '',
  });

  const formatBrlNumber = (val: any) =>
    val ? Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

  const loadInvoice = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const response = await ApiService.fiscal.getInvoice(Number(invoiceId));
      const data = response.data;
      if (!data) return;

      setInvoice(data);
      setIdentValues({
        companyEmissorId: data.companyEmissorId ? String(data.companyEmissorId) : '',
        naturezaOperacao: data.naturezaOperacao || '',
        tipoOperacao: data.tipoOperacao || '',
        finalidade: data.finalidade || '',
      });
      if (data.CompanyCliente) {
        setSelectedDestinatario({ company: data.CompanyCliente });
      }
      if (Array.isArray(data.InvoiceProduct)) {
        const mapped = data.InvoiceProduct.map((p: any) => ({
          id: p.id || p.orderItemId,
          codigo: `ITEM-${p.orderItemId || p.id}`,
          descricao: p.descricaoProduto || 'Produto Sem Descrição',
          ncmId: p.ncmId || null,
          cfopId: null,
          cfop: p.cfop || '',
          ncmCode: p.Ncm?.ncm || '',
          cst: p.cstICMS || '',
          cstPisCofins: p.cstPIS_COFINS || '',
          cstIbsCbs: p.cstIBS_CBS || '',
          classificacao: p.classificacaoTributaria || '',
          unidade: 'un',
          quantidade: p.quantidade || 0,
          vlUnit: formatBrlNumber(p.valorUnitario) || '0,00',
          vlTotal: formatBrlNumber(p.valorTotal) || '0,00',
          bcIcms: formatBrlNumber(p.baseCalculoICMS || p.valorTotal),
          vlIcms: formatBrlNumber(p.valorICMS),
          vlIpi: formatBrlNumber(p.valorIPI),
          vlIbs: formatBrlNumber(p.valorIBS),
          vlCbs: formatBrlNumber(p.valorCBS),
          aliqIcms: p.aliquotaICMS ?? '',
          aliqIpi: p.aliquotaIPI ?? '',
          _lastLookupHash: '',
        }));
        setItems(mapped);
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da nota fiscal:', error);
      setFeedback({ type: 'error', message: 'Erro ao carregar detalhes da nota fiscal.', isOpen: true, statusOk: false });
    }
  }, [invoiceId, setFeedback]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  // Sincroniza com o Nota Gateway (consultarStatus grava EMITIDA/NEGADA) e então relê.
  // Só reler (loadInvoice) deixaria a nota presa em EMITINDO — o status não muda sozinho.
  const syncStatus = useCallback(async () => {
    if (!invoiceId) return;
    try {
      await ApiService.fiscal.nfeStatus(Number(invoiceId));
    } catch (error) {
      console.error('Erro ao consultar status da NF-e:', error);
    }
    await loadInvoice();
  }, [invoiceId, loadInvoice]);

  useEmissionPolling({ isEmitindo: invoice?.status === 'EMITINDO', onTick: syncStatus });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await ApiService.companies.findAll();
        const data = Array.isArray(response.data) ? response.data : response.data.data;
        setCompanies(data || []);
      } catch (error) {
        console.error('Erro ao buscar empresas:', error);
      }
    };
    fetchCompanies();

    const fetchEmissoras = async () => {
      try {
        const response = await ApiService.fiscal.getCompanyGateways();
        const data = Array.isArray(response.data) ? response.data : response.data.data;
        setEmissoras(data || []);
      } catch (error) {
        console.error('Erro ao buscar empresas emissoras:', error);
      }
    };
    fetchEmissoras();

    const fetchCfops = async () => {
      try {
        const response = await ApiService.fiscal.getCfops();
        const data = Array.isArray(response.data) ? response.data : response.data.data;
        setCfops(data || []);
      } catch (error) {
        console.error('Erro ao buscar CFOPs:', error);
      }
    };
    fetchCfops();
  }, []);

  const destStateId = (() => {
    const c = selectedDestinatario?.company as any;
    return (
      c?.stateId ||
      c?.state?.id ||
      c?.Address?.Neighborhood?.City?.stateId ||
      c?.address?.neighborhood?.city?.stateId ||
      null
    );
  })();

  // Emissor selecionado — o cenário fiscal (CFOP/NCM/lookup) depende dele.
  const companyEmissorId = identValues.companyEmissorId ? Number(identValues.companyEmissorId) : null;

  // Trocar a emissora invalida os caches de CFOP/NCM e força novo lookup: as opções
  // e a tributação são por emissor. Sem isso, ficariam presas no emissor anterior.
  useEffect(() => {
    setCfopsPerItem(new Map());
    setNcmOptions([]);
    setItems((prev) => prev.map((p) => ({ ...p, _lastLookupHash: '' })));
  }, [companyEmissorId]);

  useItemTaxResolution({ items, setItems, cfops, setCfopsPerItem, destStateId, isEmitida, companyEmissorId });

  const fetchNcmsForItem = async (item: InvoiceItem) => {
    if (!item.cfopId || !destStateId || !companyEmissorId) { setNcmOptions([]); return; }
    try {
      const response = await ApiService.fiscal.getNcms({ cfopId: item.cfopId, stateId: destStateId, companyEmissorId });
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      setNcmOptions(data || []);
    } catch {
      setNcmOptions([]);
    }
  };

  const handleEmitir = async () => {
    if (!invoiceId) return;

    if (!identValues.companyEmissorId) {
      setFeedback({ message: 'Selecione a empresa emissora antes de emitir.', isOpen: true, statusOk: false });
      return;
    }
    if (!identValues.naturezaOperacao) {
      setFeedback({ message: 'Selecione a natureza da operação antes de emitir.', isOpen: true, statusOk: false });
      return;
    }

    const itensSemCfop = items.filter((i) => !i.cfop);
    if (itensSemCfop.length > 0) {
      setFeedback({ message: `CFOP não informado para: ${itensSemCfop.map((i) => i.descricao).join(', ')}`, isOpen: true, statusOk: false });
      return;
    }
    const itensSemNcm = items.filter((i) => !i.ncmId);
    if (itensSemNcm.length > 0) {
      setFeedback({ message: `NCM não informado para: ${itensSemNcm.map((i) => i.descricao).join(', ')}`, isOpen: true, statusOk: false });
      return;
    }

    try {
      await ApiService.fiscal.updateInvoice(Number(invoiceId), {
        naturezaOperacao: identValues.naturezaOperacao || undefined,
        finalidade: identValues.finalidade || undefined,
        tipoOperacao: identValues.tipoOperacao || undefined,
        companyEmissorId: identValues.companyEmissorId ? Number(identValues.companyEmissorId) : undefined,
        items: items.map((i) => ({ itemId: Number(i.id), cfop: i.cfop || undefined, ncmId: i.ncmId || undefined })),
      });
      await ApiService.fiscal.emitir(Number(invoiceId));
      setFeedback({ message: 'Emissão iniciada. Aguardando autorização da SEFAZ...', isOpen: true, statusOk: true });
      loadInvoice();
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      setFeedback({ message: Array.isArray(msg) ? msg.join('; ') : (msg || 'Erro ao emitir a nota.'), isOpen: true, statusOk: false });
    }
  };

  const handleCancelar = async () => {
    if (!invoiceId) return;
    try {
      await ApiService.fiscal.cancelar(Number(invoiceId));
      setFeedback({ message: 'Cancelamento solicitado.', isOpen: true, statusOk: true });
      loadInvoice();
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao cancelar a nota.', isOpen: true, statusOk: false });
    }
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceId) return;
    setDeleting(true);
    try {
      await ApiService.fiscal.deleteInvoice(Number(invoiceId));
      setFeedback({ message: 'Nota excluída.', isOpen: true, statusOk: true });
      navigate('/panel/invoice');
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao excluir a nota.', isOpen: true, statusOk: false });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <MainContainer style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <DestinatarioSection
            companies={companies}
            selectedDestinatario={selectedDestinatario}
            onSelect={setSelectedDestinatario}
            readOnly={isEmitida}
          />
          <ItensSection
            items={items}
            setItems={setItems}
            cfops={cfops}
            cfopsPerItem={cfopsPerItem}
            destStateId={destStateId}
            isEmitida={isEmitida}
            onRemove={setRemoveItemTarget}
            fetchNcmsForItem={fetchNcmsForItem}
            ncmOptions={ncmOptions}
            onAddItem={podeEditarItens && invoice?.orderId ? () => setShowAddItems(true) : undefined}
            semEmissor={!companyEmissorId}
          />
          <PagamentoSection invoice={invoice} />
          <FreteSection invoice={invoice} />
          <InfoAdicionaisSection invoice={invoice} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', position: 'sticky', top: '0', alignSelf: 'start' }}>
          <EmissaoSection
            invoice={invoice}
            invoiceId={invoiceId ?? null}
            onEmitir={handleEmitir}
            onCancelar={handleCancelar}
            onExcluir={podeEditarItens ? () => setShowDeleteConfirm(true) : undefined}
            onVoltar={() => navigate(-1)}
          />
          <IdentificacaoSection
            emissoras={emissoras}
            invoice={invoice}
            value={identValues}
            onChange={(next) => setIdentValues((prev) => ({ ...prev, ...next }))}
            readOnly={isEmitida}
          />
          <TotaisSection items={items} />
        </div>
      </MainContainer>

      {removeItemTarget && (
        <RemoveInvoiceItemModal
          item={removeItemTarget}
          onCancel={() => setRemoveItemTarget(null)}
          onConfirm={async (quantidade) => {
            try {
              await ApiService.fiscal.removeInvoiceItem(Number(invoiceId), removeItemTarget.id, quantidade);
            } catch (error: any) {
              setFeedback({ message: error?.response?.data?.message || 'Erro ao remover item.', isOpen: true, statusOk: false });
            } finally {
              // Sempre re-sincroniza a tela com o banco: se a remoção falhar, o item
              // reaparece — a tela nunca fica dessincronizada do que está persistido.
              setRemoveItemTarget(null);
              loadInvoice();
            }
          }}
        />
      )}

      {showAddItems && invoiceId && invoice?.orderId && (
        <AddInvoiceItemsModal
          invoiceId={Number(invoiceId)}
          orderId={Number(invoice.orderId)}
          onClose={() => setShowAddItems(false)}
          onAdded={() => {
            setShowAddItems(false);
            setFeedback({ message: 'Itens adicionados à nota.', isOpen: true, statusOk: true });
            loadInvoice();
          }}
          onError={(msg) => setFeedback({ message: msg, isOpen: true, statusOk: false })}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Excluir nota"
        message={`Tem certeza que deseja excluir esta nota em rascunho? Esta ação não pode ser desfeita.`}
        actions={[
          { label: 'Voltar', onClick: () => setShowDeleteConfirm(false), variant: 'outline' },
          {
            label: deleting ? 'Excluindo…' : 'Excluir nota',
            onClick: handleDeleteInvoice,
            variant: 'danger',
          },
        ]}
      />
    </>
  );
};

export default InvoiceForm;
